import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { bulkMoveChannels } from '../../api/channelBulkAdvanced';
import { getApiErrorMessage } from '../../api/http';
import { fetchPlaylistList, type PlaylistRow } from '../../api/playlists';
import { fetchCategories } from '../../api/categoriesApi';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { useBasicEditorToolContext } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';

type Mode = 'move' | 'copy';

export default function MoveCopyChannelsModal({
  show,
  onClose,
  initialMode,
}: {
  show: boolean;
  onClose: () => void;
  initialMode: Mode;
}) {
  const { showToast } = useToast();
  const { refreshEditor } = useUndoRedo();
  const { selectedIds, gridActive } = useBasicEditorToolContext();
  const { playlistId } = usePlaylist();
  const [playlists, setPlaylists] = useState<PlaylistRow[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [targetId, setTargetId] = useState('');
  const [targetCategory, setTargetCategory] = useState('');
  const [mode, setMode] = useState<Mode>(initialMode);
  const [step, setStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);

  const fromNum = playlistId ? parseInt(playlistId, 10) : 0;

  const load = useCallback(async () => {
    try {
      const rows = await fetchPlaylistList();
      setPlaylists(Array.isArray(rows) ? rows : []);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to load playlists.'), 'error');
    }
  }, [showToast]);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setMode(initialMode);
    setTargetId('');
    setCategories([]);
    setTargetCategory('');
    void load();
  }, [show, initialMode, load]);

  useEffect(() => {
    const toId = parseInt(targetId, 10);
    if (!Number.isFinite(toId) || toId <= 0) {
      setCategories([]);
      setTargetCategory('');
      return;
    }
    void (async () => {
      try {
        const rows = await fetchCategories(String(toId));
        const names = rows.map((r) => r.name).filter((n) => !!String(n || '').trim());
        setCategories(names);
        setTargetCategory((prev) => (prev && names.includes(prev) ? prev : names[0] || ''));
      } catch (e) {
        setCategories([]);
        setTargetCategory('');
        showToast(getApiErrorMessage(e, 'Failed to load target categories.'), 'error');
      }
    })();
  }, [targetId, showToast]);

  const targetOptions = useMemo(
    () => playlists.filter((p) => p.id !== fromNum),
    [playlists, fromNum],
  );

  async function apply() {
    if (!playlistId || fromNum <= 0) {
      showToast('Select a source playlist first.', 'warning');
      return;
    }
    if (!gridActive || selectedIds.length === 0) {
      showToast('Select channels in the Basic editor.', 'warning');
      return;
    }
    const toId = parseInt(targetId, 10);
    if (!Number.isFinite(toId) || toId <= 0) {
      showToast('Choose target playlist.', 'warning');
      return;
    }
    const target_category = targetCategory.trim();
    if (!target_category) {
      showToast('Choose target category.', 'warning');
      return;
    }

    const payload = {
      from_playlist_id: fromNum,
      to_playlist_id: toId,
      channel_ids: selectedIds.map(Number),
      mode,
      target_category,
    };

    const isCopy = mode === 'copy';

    setBusy(true);
    try {
      const res = await bulkMoveChannels(payload);
      if (!res.success) {
        showToast(res.message || 'Operation failed.', 'error');
        return;
      }
      const existingNames = (res.existing_url_channel_names || []).filter((x) => String(x || '').trim() !== '');
      const existingCount = Number(res.existing_url_count || 0);
      const previewLimit = 8;
      const previewNames = existingNames.slice(0, previewLimit);
      const remaining = Math.max(0, existingNames.length - previewLimit);
      const baseMessage =
        res.message ||
        (isCopy
          ? `Copied ${res.copied ?? selectedIds.length} channel(s).`
          : `Moved ${res.moved ?? selectedIds.length} channel(s).`);
      const duplicateMessage =
        existingCount > 0
          ? previewNames.length > 0
            ? ` URL already existed in target playlist for: ${previewNames.join(', ')}${remaining > 0 ? ` and ${remaining} more` : ''}.`
            : ` URL already existed in target playlist for ${existingCount} channel(s).`
          : '';
      const toastMessage = `${baseMessage}${duplicateMessage}`.trim();
      const toastType = isCopy && (res.copied ?? 0) === 0 ? 'warning' : 'success';
      showToast(toastMessage || 'Done.', toastType);
      onClose();
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server error.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Move streams in another playlist"
      size="md"
      modalId="moveCopyChannelsModal"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {step === 2 ? (
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)} disabled={busy}>
              Previous
            </button>
          ) : null}
          {step === 1 ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => {
                const toId = parseInt(targetId, 10);
                if (!Number.isFinite(toId) || toId <= 0) {
                  showToast('Choose target playlist.', 'warning');
                  return;
                }
                setStep(2);
              }}
            >
              Next
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void apply()}>
              {busy ? <span className="spinner-border spinner-border-sm" /> : mode === 'copy' ? 'Copy' : 'Move'}
            </button>
          )}
        </div>
      }
    >
      <div className="pb-2 px-1">
        {step === 1 ? (
          <div>
            <label className="form-label">Select a playlist</label>
            <select className="form-select" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">No playlist selected</option>
              {targetOptions.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <label className="form-label">Select a category in target playlist</label>
              <select
                className="form-select"
                value={targetCategory}
                onChange={(e) => setTargetCategory(e.target.value)}
              >
                <option value="">No category selected</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label d-block">Action</label>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="mcMode"
                  id="mcMove"
                  checked={mode === 'move'}
                  onChange={() => setMode('move')}
                />
                <label className="form-check-label" htmlFor="mcMove">
                  Move selected streams
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="mcMode"
                  id="mcCopy"
                  checked={mode === 'copy'}
                  onChange={() => setMode('copy')}
                />
                <label className="form-check-label" htmlFor="mcCopy">
                  Copy selected streams
                </label>
              </div>
            </div>
          </>
        )}
        <br />
        <p className="small text-muted mb-0">
          Selected in Basic editor: <strong>{selectedIds.length}</strong>
        </p>
      </div>
    </SimpleModal>
  );
}
